import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ContactDetails from '../components/ContactDetails';
import ContactForm from '../components/ContactForm';
import CtaBand from '../components/CtaBand';
import PageHero from '../components/PageHero';
import Seo from '../components/Seo';
import { DEMO_PRODUCT_OPTIONS, PRODUCTS, SITE, getProductById } from '../config/site';

export default function Demo() {
  const [params] = useSearchParams();
  const productId = params.get('product');
  const selected = useMemo(() => getProductById(productId), [productId]);

  const defaultServiceType = useMemo(() => {
    if (!selected) return '';
    const match = DEMO_PRODUCT_OPTIONS.find((opt) => opt.value === selected.id);
    return match?.label || '';
  }, [selected]);

  const defaultSubject = selected ? `${selected.name} demo request` : 'Product demo request';

  return (
    <>
      <Seo
        title="Request a Demo"
        description={`Request a demo of SutraMQ, SutraACS, MGOpen NMS, or custom AI/cloud software from ${SITE.legalName}.`}
        path="/demo"
      />

      <PageHero
        label="Demo"
        title="See our platforms in action"
        tagline={SITE.domain}
        lead="Schedule a walkthrough of SutraMQ, SutraACS, MGOpen NMS, or discuss a custom AI-led build with our engineering team."
      />

      <section className="section section--band">
        <div className="container">
          <div className="demo-product-strip">
            {PRODUCTS.filter((p) => p.demoAvailable || p.isFlagship).map((product) => (
              <Link
                key={product.id}
                to={`/demo?product=${product.id}`}
                className={`demo-product-pill${productId === product.id ? ' is-active' : ''}`}
                style={{ '--product-accent': product.accent }}
              >
                {product.name}
              </Link>
            ))}
          </div>

          <div className="contact-grid contact-grid--form">
            <div className="card card--glass">
              <h3>Request a demo</h3>
              <p className="contact-card__intro">
                Tell us your use case, expected scale, and timeline.{' '}
                {selected && (
                  <>
                    Selected: <strong>{selected.name}</strong> — {selected.tagline}.
                  </>
                )}
              </p>
              <ContactForm
                key={productId || 'general'}
                showProductSelect
                defaultServiceType={defaultServiceType}
                defaultSubject={defaultSubject}
                subjectPrefix="[Demo] "
                submitLabel="Request demo"
              />
            </div>
            <div className="card card--glass contact-card">
              <ContactDetails showFlagshipNote={false} />
              <p className="contact-card__note">
                {SITE.legalName} · {SITE.location}
              </p>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        creative
        title="Prefer email?"
        description={`Write to ${SITE.email} with subject "${defaultSubject}" and we will schedule a session.`}
        primaryLabel="Email us"
        primaryHref={`mailto:${SITE.email}?subject=${encodeURIComponent(defaultSubject)}`}
        primaryExternal
        secondaryLabel="All products"
        secondaryTo="/products"
      />
    </>
  );
}
